import { useCallback } from 'react';
import { HierarchyManager } from '../services/HierarchyManager.js';
import { LayoutCalculator } from '../services/LayoutCalculator.js';
import { calculateRect, generateId, getNodeChildren } from '../utils.js';
import { resolveNode } from '../utils/responsive.js';

// Helper to calculate new layout for a child moving into a container
const calculateChildLayoutInContainer = (childNode, containerNode, parentW, parentH, activeBreakpoint) => {
  const resolvedChild = resolveNode(childNode, activeBreakpoint);
  const childRect = calculateRect(resolvedChild, parentW, parentH);

  // Calculate new relative position
  // containerNode.x/y are relative to the common parent (parentW/parentH)
  // childRect.x/y are relative to the common parent (parentW/parentH)
  // New position is relative to the container's top-left
  const newX = childRect.x - containerNode.x;
  const newY = childRect.y - containerNode.y;

  // Clean up responsive overrides for layout since we are resetting base layout
  const newProps = { ...childNode.props };
  if (newProps.responsiveOverrides) {
    const newOverrides = { ...newProps.responsiveOverrides };
    Object.keys(newOverrides).forEach(bp => {
      const bpOver = { ...newOverrides[bp] };
      delete bpOver.layout_x;
      delete bpOver.layout_y;
      delete bpOver.layout_width;
      delete bpOver.layout_height;
      delete bpOver.layout_anchors;

      if (Object.keys(bpOver).length > 0) {
        newOverrides[bp] = bpOver;
      } else {
        delete newOverrides[bp];
      }
    });
    newProps.responsiveOverrides = newOverrides;
  }

  return {
    x: newX,
    y: newY,
    width: childRect.width,
    height: childRect.height,
    props: newProps
  };
};

export const useHierarchyOperations = ({
  activeNodes,
  selectedNodeIds,
  rootW,
  rootH,
  historyExecute,
  activeBreakpoint
}) => {
  const handleHierarchyMove = useCallback((dragIds, targetId, position) => {
    const newNodes = HierarchyManager.moveNodes(dragIds, targetId, position, activeNodes, rootW, rootH, activeBreakpoint);
    if (newNodes) {
      historyExecute(activeNodes, newNodes, selectedNodeIds, selectedNodeIds);
    }
  }, [activeNodes, selectedNodeIds, rootW, rootH, historyExecute, activeBreakpoint]);

  const handleContextMenuAction = useCallback((action, contextNode) => {
    if (action === 'put-in-container') {
      const nodesToGroupIds = selectedNodeIds.includes(contextNode.id)
        ? selectedNodeIds
        : [contextNode.id];

      if (nodesToGroupIds.length === 0) return;

      const nodesToGroup = activeNodes.filter(n => nodesToGroupIds.includes(n.id));
      if (nodesToGroup.length === 0) return;

      nodesToGroup.sort((a, b) => activeNodes.indexOf(a) - activeNodes.indexOf(b));
      const anchorNode = nodesToGroup[0];
      const parentId = anchorNode.parentId;

      const siblingsToGroup = nodesToGroup.filter(n => n.parentId === parentId);
      if (siblingsToGroup.length === 0) return;

      const finalNodeIds = siblingsToGroup.map(n => n.id);

      let parentW = rootW;
      let parentH = rootH;
      if (parentId) {
        const pRect = LayoutCalculator.measureNodeById(parentId, activeNodes, rootW, rootH, activeBreakpoint);
        parentW = pRect.width;
        parentH = pRect.height;
      }

      const bbox = LayoutCalculator.calculateBoundingBox(siblingsToGroup, activeNodes, parentW, parentH, activeBreakpoint);

      const containerId = generateId('Box');
      const newContainer = {
        id: containerId,
        type: 'Box',
        label: 'Container',
        x: bbox.x,
        y: bbox.y,
        width: bbox.width,
        height: bbox.height,
        parentId: parentId,
        anchors: { minX: 0, maxX: 0, minY: 0, maxY: 0 },
        props: { backgroundColor: 'transparent' }
      };

      const childrenUpdates = siblingsToGroup.map(node => {
        const newLayout = calculateChildLayoutInContainer(node, newContainer, parentW, parentH, activeBreakpoint);
        return {
          ...node,
          parentId: containerId,
          x: newLayout.x,
          y: newLayout.y,
          width: newLayout.width,
          height: newLayout.height,
          anchors: { minX: 0, maxX: 0, minY: 0, maxY: 0 },
          props: newLayout.props
        };
      });

      const nodesToKeep = activeNodes.filter(n => !finalNodeIds.includes(n.id));
      const anchorOriginalIndex = activeNodes.indexOf(anchorNode);

      let insertionIndex = 0;
      for (let i = 0; i < anchorOriginalIndex; i++) {
        if (!finalNodeIds.includes(activeNodes[i].id)) {
          insertionIndex++;
        }
      }

      const newNodes = [...nodesToKeep];
      newNodes.splice(insertionIndex, 0, newContainer);
      newNodes.push(...childrenUpdates);

      historyExecute(activeNodes, newNodes, selectedNodeIds, [containerId]);
    } else if (action === 'ungroup-container') {
      const container = contextNode;
      const children = getNodeChildren(activeNodes, container.id);
      
      if (children.length === 0) return;

      const parentId = container.parentId;
      let parentGlobalRect = { x: 0, y: 0, width: rootW, height: rootH };
      
      if (parentId) {
         parentGlobalRect = LayoutCalculator.measureNodeById(parentId, activeNodes, rootW, rootH, activeBreakpoint);
      }

      const childrenUpdates = children.map(child => {
          // Measure child in global space
          const childGlobalRect = LayoutCalculator.measureNodeById(child.id, activeNodes, rootW, rootH, activeBreakpoint);
          
          // Calculate new relative position in the container's parent
          const newX = childGlobalRect.x - parentGlobalRect.x;
          const newY = childGlobalRect.y - parentGlobalRect.y;

          // Clean up responsive layout overrides
          const newProps = { ...child.props };
          if (newProps.responsiveOverrides) {
            const newOverrides = { ...newProps.responsiveOverrides };
            Object.keys(newOverrides).forEach(bp => {
                const bpOver = { ...newOverrides[bp] };
                delete bpOver.layout_x;
                delete bpOver.layout_y;
                delete bpOver.layout_width;
                delete bpOver.layout_height;
                delete bpOver.layout_anchors;
                if (Object.keys(bpOver).length > 0) newOverrides[bp] = bpOver;
                else delete newOverrides[bp];
            });
            newProps.responsiveOverrides = newOverrides;
          }

          return {
              ...child,
              parentId: parentId,
              x: newX,
              y: newY,
              width: childGlobalRect.width,
              height: childGlobalRect.height,
              anchors: { minX: 0, maxX: 0, minY: 0, maxY: 0 },
              props: newProps
          };
      });

      // Construct new node list
      // Remove container, insert children at container's index
      const containerIndex = activeNodes.findIndex(n => n.id === container.id);
      const newNodes = [...activeNodes];
      
      // Remove container
      newNodes.splice(containerIndex, 1);
      
      // Remove old children (they are updated and re-inserted)
      // Actually, we need to map the updates to the existing objects or replace them?
      // Since children are also in activeNodes, we need to replace them.
      // But we also moved them.
      // Strategy: Filter out container, map children to new updates.
      
      // Let's do it cleanly:
      // 1. Filter out container.
      const nodesWithoutContainer = activeNodes.filter(n => n.id !== container.id);
      
      // 2. Map the updates.
      const updatedNodes = nodesWithoutContainer.map(n => {
          const update = childrenUpdates.find(u => u.id === n.id);
          return update || n;
      });

      historyExecute(activeNodes, updatedNodes, selectedNodeIds, childrenUpdates.map(c => c.id));
    }
  }, [selectedNodeIds, activeNodes, rootW, rootH, historyExecute, activeBreakpoint]);

  return {
    handleHierarchyMove,
    handleContextMenuAction
  };
};
